import { DIALOGS_NAMES } from '~/constants';
import { showDialog, hideDialog } from '~/store/slices/dialogsSlice';
export { closeAllDialogs } from '~/store/slices/dialogsSlice';

import type { Action } from '@reduxjs/toolkit';
import type { ClanInfoType } from '~/Actions/ActionClanProfile';
import type { ClanTask, ClanTaskReward } from '~/Actions/ActionClanTasks';
import type { ClanInvitationMinimal, IInvite } from '~/Actions/ActionInvites';
import type { ClanRecommendation } from '~/Actions/ActionRecommendations';
import type { CLAN_INFO_TABS } from '~/Components/Dialogs/ClanInfoDialog/ClanInfoDialog';
import type { IClanStarsReward } from '~/Reducers/ReducerClanStars';
import type { RegularReward as IRegularReward } from '~/Reducers/ReducerTreasury';
import type { ENTRY_SOURCE } from '~/store/slices/settingsSlice';
import type { ClanInSearch, ClanSearchResultItem, IBattle, IClanMember, Transaction } from '~/types/declaration';
import type { INation } from '~/types/preloaded';

export const showSendApplicationDialog = (
  clan: ClanInfoType | ClanRecommendation | ClanInSearch | ClanSearchResultItem,
  sourceName: ENTRY_SOURCE,
  shouldFetchApplications: boolean = false,
  onSend: ((info: string) => void) | undefined = undefined,
): Action =>
  showDialog({
    name: DIALOGS_NAMES.SEND_APPLICATION_DIALOG,
    data: {
      clan: clan,
      source: sourceName,
      shouldFetchApplications: shouldFetchApplications,
      onSend: onSend,
    },
    options: {
      size: 'small',
    },
  });

export const showInviteAcceptDialog = (invite: ClanInvitationMinimal): Action =>
  showDialog({
    name: DIALOGS_NAMES.INVITE_ACCEPT_DIALOG,
    data: {
      invite: invite,
    },
    options: {
      size: 'small',
    },
  });

export const showInviteCancelDialog = (invite: IInvite): Action =>
  showDialog({
    name: DIALOGS_NAMES.INVITE_CANCEL_DIALOG,
    data: {
      invite: invite,
    },
    options: {
      size: 'small',
    },
  });

export const showMonumentAchievementsDialog = (clanId: number): Action =>
  showDialog({
    name: DIALOGS_NAMES.MONUMENT_ACHIEVEMENTS_DIALOG,
    data: {
      clanId: clanId,
    },
    options: {
      size: 'overlay',
      isOverlay: true,
    },
  });

export const showMembersOperationRemoveDialog = (membersToRemove: IClanMember[]): Action =>
  showDialog({
    name: DIALOGS_NAMES.MEMBERS_OPERATION_REMOVE,
    data: {
      membersToRemove: membersToRemove,
    },
    options: {
      size: 'small',
    },
  });

export const showMembersOperationChangeCommanderDialog = (newCommander: IClanMember): Action =>
  showDialog({
    name: DIALOGS_NAMES.MEMBERS_OPERATION_CHANGE_COMMANDER,
    data: {
      newCommander: newCommander,
    },
    options: {
      size: 'small',
    },
  });

export const showMembersOperationChangeRoleDialog = (memberToChangeId?: number): Action =>
  showDialog({
    name: DIALOGS_NAMES.MEMBERS_OPERATION_CHANGE_ROLE,
    data: {
      memberToChangeId,
    },
    options: {
      size: 'small',
    },
  });

export const showBuildTreasuryDialog = (): Action =>
  showDialog({
    name: DIALOGS_NAMES.BUILD_TREASURY_DIALOG,
    options: {
      size: 'large',
    },
  });

export const showRulesDialog = (): Action =>
  showDialog({
    name: DIALOGS_NAMES.RULES_DIALOG,
    data: {},
    options: {
      size: 'large',
    },
  });

export const showClanLeaveDialog = (): Action =>
  showDialog({
    name: DIALOGS_NAMES.CLAN_LEAVE_DIALOG,
    options: {
      size: 'large',
    },
  });

export const showRecommendationsClanSettingsDialog = (): Action =>
  showDialog({
    name: DIALOGS_NAMES.CLAN_CLANREC_SETTINGS_DIALOG,
    options: {
      size: 'large',
    },
  });

export const showNameEditDialog = (): Action =>
  showDialog({
    name: DIALOGS_NAMES.CLAN_NAME_EDIT_DIALOG,
    options: {
      size: 'large',
    },
  });

export const showDistributionDialog = (): Action =>
  showDialog({
    name: DIALOGS_NAMES.DISTRIBUTION_DIALOG,
    options: {
      size: 'small',
    },
  });

export const showDistributionResultDialog = (transaction: Transaction): Action =>
  showDialog({
    name: DIALOGS_NAMES.DISTRIBUTION_RESULT_DIALOG,
    data: {
      transaction,
    },
    options: {
      size: 'small',
    },
  });

export const hideDistributionResultDialog = (): Action => {
  return hideDialog([DIALOGS_NAMES.DISTRIBUTION_RESULT_DIALOG]);
};

export const showRegularRewardConfirmDialog = (reward: IRegularReward): Action =>
  showDialog({
    name: DIALOGS_NAMES.DISTRIBUTION_REWARD_CONFIRM_DIALOG,
    data: {
      reward,
    },
    options: {
      size: 'small',
    },
  });

export const showCWarsCalendarDialog = (): Action =>
  showDialog({
    name: DIALOGS_NAMES.CWARS_CALENDAR_DIALOG,
    options: {
      size: 'extra-large',
    },
  });

export const showCWarsLastRoundDialog = (): Action =>
  showDialog({
    name: DIALOGS_NAMES.CWARS_LAST_ROUND_DIALOG,
    options: {
      size: 'extra-large',
      isOverlay: false,
    },
  });

export const showCWarsStatsDialog = (...[nation, type]: [INation, string] | undefined[]): Action =>
  showDialog({
    name: DIALOGS_NAMES.CWARS_STATS_DIALOG,
    data:
      nation && type
        ? {
            selectedStatisticNation: nation,
            selectedStatisticType: type,
          }
        : undefined,
    options: {
      size: 'extra-large',
    },
  });

export const showCWarsAttemptsDialog = (selectedAttemptsUserId: number, battles: IBattle[]): Action => {
  const userBattles = battles.some((battle) => {
    return battle.spaId === selectedAttemptsUserId;
  });

  return showDialog({
    name: DIALOGS_NAMES.CWARS_ATTEMPTS_DIALOG,
    data: {
      selectedAttemptsUserId: selectedAttemptsUserId,
    },
    options: {
      size: userBattles ? 'extra-large' : 'small',
    },
  });
};

export const showClansInformerDialog = (clanId: number | undefined, source: ENTRY_SOURCE): Action =>
  showDialog({
    name: DIALOGS_NAMES.CLANS_INFORMER_DIALOG,
    data: {
      clanId: clanId,
      source: source,
    },
    options: {
      size: 'fullsize',
    },
  });

export const showExternalNavigationDialog = (link: string): Action => {
  return showDialog({
    name: DIALOGS_NAMES.EXTERNAL_NAVIGATION_DIALOG,
    data: {
      externalLink: link,
    },
    options: {
      size: 'large',
    },
  });
};

type IShowClanInfoDialog = {
  clanId: number;
  initialTab?: CLAN_INFO_TABS;
  source?: ENTRY_SOURCE;
  isInEditMode?: boolean;
  isOpenedFromClanBase?: boolean;
};
export const showClanInfoDialog = ({
  clanId,
  initialTab,
  isInEditMode = false,
  isOpenedFromClanBase = false,
  source = undefined,
}: IShowClanInfoDialog): Action => {
  return showDialog({
    name: DIALOGS_NAMES.CLAN_INFO_DIALOG,
    data: {
      clanId,
      initialTab,
      isInEditMode,
      isOpenedFromClanBase,
      source,
    },
  });
};

export const showCStarsRewardsDialog = (newRewards: IClanStarsReward[]): Action =>
  showDialog({
    name: DIALOGS_NAMES.CSTARS_REWARDS_DIALOG,
    data: {
      newRewards,
    },
    options: {
      size: 'worship',
    },
  });

export const showCStarsRefinedInfoDialog = (): Action =>
  showDialog({
    name: DIALOGS_NAMES.CSTARS_REFINED_INFO_DIALOG,
  });

export const showClanRecLanguagesDialog = (
  languagesMap: Record<string, boolean>,
  updateLanguages: (languages: string[]) => void,
): Action =>
  showDialog({
    name: DIALOGS_NAMES.LANGUAGES_DIALOG,
    data: {
      languagesMap,
      updateLanguages,
    },
    options: {
      size: 'worship',
    },
  });

export const showClanTasksRewardsDialog = (tasks: ClanTask[], aggregatedReward: ClanTaskReward): Action =>
  showDialog({
    name: DIALOGS_NAMES.CLAN_TASKS_REWARDS_DIALOG,
    data: {
      tasks,
      aggregatedReward,
    },
    options: {
      size: 'worship',
    },
  });
